import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./shared";

const BASE = "https://customer-assets-jt897jd0.emergentagent.net/job_tanya-cleaning-yeg/artifacts";
const slides = [
  { url: `${BASE}/vxam2ekk_1.jpg`, caption: "Full floor deep clean & restoration" },
  { url: `${BASE}/bapcy0ul_2.jpg`, caption: "Hardwood floor detailed polish" },
  { url: `${BASE}/hq33inb5_3.jpg`, caption: "Hallway post-construction cleanup" },
  { url: `${BASE}/2dc229zc_4.jpg`, caption: "Bathroom fixtures brought back to shine" },
  { url: `${BASE}/50smqxwg_5.jpg`, caption: "Kitchen cabinet & drawer detailing" },
  { url: `${BASE}/us834tgv_6.jpg`, caption: "Toilet bowl deep sanitizing" },
];

const n = slides.length;

export default function WorkGallery() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const paginate = useCallback((d) => setIndex((i) => (i + d + n) % n), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => paginate(1), 4500);
    return () => clearInterval(t);
  }, [paused, paginate, index]);

  const styleFor = (i) => {
    let o = i - index;
    if (o > n / 2) o -= n;
    if (o < -n / 2) o += n;

    if (o === 0)
      return { transform: "translateX(-50%) rotateY(0deg) scale(1)", opacity: 1, zIndex: 30, filter: "brightness(1)" };
    if (o === -1)
      return { transform: "translateX(-84%) rotateY(38deg) scale(0.8)", opacity: 0.55, zIndex: 20, filter: "brightness(0.8)" };
    if (o === 1)
      return { transform: "translateX(-16%) rotateY(-38deg) scale(0.8)", opacity: 0.55, zIndex: 20, filter: "brightness(0.8)" };
    return { transform: "translateX(-50%) scale(0.6)", opacity: 0, zIndex: 0, pointerEvents: "none" };
  };

  return (
    <section id="our-work" data-testid="work-gallery" className="bg-[#FAFAFA] py-24 md:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm uppercase tracking-[0.25em] text-pink-dark font-semibold mb-4">Our Work</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-teal font-medium">See the difference</h2>
          <p className="mt-5 text-lg text-teal-soft leading-relaxed">
            Real before &amp; after results from homes and spaces across Edmonton.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {/* coverflow stage */}
            <div
              className="relative mx-auto h-[190px] sm:h-[300px] lg:h-[380px]"
              style={{ perspective: "1400px" }}
            >
              {slides.map((s, i) => (
                <div
                  key={i}
                  data-testid={`work-slide-${i}`}
                  onClick={() => setIndex(i)}
                  style={styleFor(i)}
                  className="absolute top-0 left-1/2 h-full w-[330px] sm:w-[520px] lg:w-[660px] rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(43,81,86,0.22)] ring-1 ring-white cursor-pointer transition-all duration-500 ease-out"
                >
                  <img
                    src={s.url}
                    alt={s.caption}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {/* arrows */}
            <button
              data-testid="work-prev"
              onClick={() => paginate(-1)}
              aria-label="Previous"
              className="absolute top-1/2 -translate-y-1/2 left-0 md:-left-2 z-40 w-12 h-12 rounded-full bg-white text-teal shadow-lg ring-1 ring-blush flex items-center justify-center hover:bg-teal hover:text-white transition-all duration-300 hover:scale-110"
            >
              <ChevronLeft size={22} strokeWidth={2} />
            </button>
            <button
              data-testid="work-next"
              onClick={() => paginate(1)}
              aria-label="Next"
              className="absolute top-1/2 -translate-y-1/2 right-0 md:-right-2 z-40 w-12 h-12 rounded-full bg-white text-teal shadow-lg ring-1 ring-blush flex items-center justify-center hover:bg-teal hover:text-white transition-all duration-300 hover:scale-110"
            >
              <ChevronRight size={22} strokeWidth={2} />
            </button>

            {/* caption */}
            <div className="h-8 mt-8 text-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={index}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  className="font-serif-display text-lg sm:text-xl text-teal"
                >
                  {slides[index].caption}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* dots */}
            <div className="flex items-center justify-center gap-2.5 mt-5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  data-testid={`work-dot-${i}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-8 bg-teal" : "w-2.5 bg-pink/50 hover:bg-pink"
                  }`}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
